"use client";

import { useEffect, useEffectEvent } from "react";

function normaliseOptions(
  options?: boolean | AddEventListenerOptions,
  otherOptions: AddEventListenerOptions = {},
): AddEventListenerOptions {
  if (typeof options === "undefined") {
    return otherOptions;
  }

  if (typeof options === "boolean") {
    return { capture: options, ...otherOptions };
  }

  return { ...options, ...otherOptions };
}

export function useWindowEvent<EventType extends keyof WindowEventMap>(
  type: EventType,
  listener: (this: Window, ev: WindowEventMap[EventType]) => unknown,
  options?: AddEventListenerOptions,
): void {
  // useEffectEvent creates a stable reference to the callback
  // that always calls the latest version of the listener
  const stableListener = useEffectEvent(listener);

  useEffect(() => {
    // Create an abort controller to abort the event listener if the hook is re-rendered
    const controller = new AbortController();
    const { signal } = controller;
    const normalisedOptions = normaliseOptions(options, { signal });

    window.addEventListener(type, stableListener, normalisedOptions);
    return () => {
      controller.abort();
      window.removeEventListener(type, stableListener, normalisedOptions);
    };
  }, [type, options]);
}
